import React, { Component } from 'react';
import { Button, Table, Dropdown, InputGroup, FormControl, Badge } from 'react-bootstrap';
import Pagination from 'react-js-pagination';
import SearchSharpIcon from '@material-ui/icons/SearchSharp';
import MoreVertSharpIcon from '@material-ui/icons/MoreVertSharp';
import { PiArrowLeft, PiFileCsvDuotone } from 'react-icons/pi';
import Loader from '../../component/loader/loader';
import axios from '../../axios';
import apiError from '../../error';
import { toast } from 'react-toastify';
import { hypenateToNormal } from '../../function';
import './report.css';

const DEFAULT_MODULES = ['user', 'designation', 'department', 'location'];
const DEFAULT_REPORT_META = {
    'link-click-rate': {
        title: 'Link Click Rate',
        availableModules: ['user', 'designation', 'department', 'location'],
    },
    'phish-risk-rate': {
        title: 'Phish Risk Rate',
        availableModules: ['user', 'designation', 'department', 'location'],
    },
    'attack-vector-analysis': {
        title: 'Attack Vector Analysis',
        availableModules: ['attack-vector'],
    },
    'repeat-offender-analysis': {
        title: 'Repeat Offender Analysis',
        availableModules: ['Users', 'department', 'designation', 'location'],
    },
};

export default class DefaultReportPreview extends Component {
    constructor(props) {
        super(props);
        const reportName = props.match?.params?.reportName || '';
        const routeMeta = this.getRouteMeta(reportName, props.location?.state);
        const availableModules = this.normalizeModules(routeMeta.availableModules);

        this.state = {
            reportName,
            reportTitle: routeMeta.reportTitle,
            availableModules,
            activeModule: availableModules[0],
            dynamicHeader: [],
            dynamicHeaderKeys: [],
            dynamicReportList: [],
            loading: true,
            search: '',
            activePage: 1,
            limit: 15,
            count: 0,
        };
    }

    componentDidMount() {
        this.fetchDefaultReport();
    }

    componentDidUpdate(prevProps) {
        const prevReportName = prevProps.match?.params?.reportName;
        const nextReportName = this.props.match?.params?.reportName;

        if (prevReportName !== nextReportName) {
            const routeMeta = this.getRouteMeta(nextReportName || '', this.props.location?.state);
            const availableModules = this.normalizeModules(routeMeta.availableModules);
            this.setState({
                reportName: nextReportName || '',
                reportTitle: routeMeta.reportTitle,
                availableModules,
                activeModule: availableModules[0],
                activePage: 1,
                search: '',
                dynamicHeader: [],
                dynamicHeaderKeys: [],
                dynamicReportList: [],
            }, this.fetchDefaultReport);
        }
    }

    getRouteMeta = (reportName = '', locationState = {}) => {
        const reportMeta = DEFAULT_REPORT_META[reportName] || {};
        const routeModules = locationState?.availableModules;

        return {
            reportTitle: locationState?.reportTitle || reportMeta.title || hypenateToNormal(reportName),
            availableModules: Array.isArray(routeModules) && routeModules.length
                ? routeModules
                : reportMeta.availableModules || DEFAULT_MODULES,
        };
    }

    normalizeModule = (module = '') => {
        if (module === 'Users') {
            return 'Users';
        }

        const normalized = String(module).trim().toLowerCase().replace(/\s+/g, '-');
        const moduleMap = {
            user: 'user',
            department: 'department',
            location: 'location',
            designation: 'designation',
            'attack-vector': 'attack-vector',
            'repeat-offender': 'repeat-offender',
            users: 'Users',
            repeatoffendedusers: 'Users',
        };

        return moduleMap[normalized] || normalized;
    }

    normalizeModules = (modules = []) => {
        const normalized = modules.map((module) => this.normalizeModule(module)).filter(Boolean);
        return Array.from(new Set(normalized));
    }

    formatModuleLabel = (module = '') => {
        return String(module || '')
            .replace(/-/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/\b\w/g, (c) => c.toUpperCase())
            .trim();
    }

    getNormalizedKeyVariants = (header = '') => {
        const normalized = String(header || '').trim();
        const camelized = normalized
            .replace(/\s+/g, ' ')
            .split(' ')
            .map((part, index) => {
                const cleanPart = part.replace(/[^a-zA-Z0-9]/g, '');
                if (!cleanPart) return '';
                if (index === 0) return cleanPart.charAt(0).toUpperCase() + cleanPart.slice(1);
                return cleanPart.charAt(0).toUpperCase() + cleanPart.slice(1);
            })
            .join('');

        return [
            normalized,
            normalized.toLowerCase(),
            normalized.toUpperCase(),
            normalized.replace(/\s+/g, ''),
            normalized.replace(/\s+/g, '_'),
            normalized.replace(/\s+/g, '-'),
            camelized,
        ].filter(Boolean);
    }

    getCellValue = (row, header) => {
        const variants = this.getNormalizedKeyVariants(header);
        const matchedKey = variants.find((key) => Object.prototype.hasOwnProperty.call(row, key));
        const value = matchedKey ? row[matchedKey] : undefined;
        return value === undefined || value === null || value === '' ? '---' : value;
    }

    resolveDynamicHeaders = (headers, reportList) => {
        if (Array.isArray(headers) && headers.length > 0) {
            return headers;
        }
        if (!Array.isArray(reportList) || reportList.length === 0) {
            return [];
        }
        const keys = [];
        const seen = new Set();
        reportList.forEach((row) => {
            if (row && typeof row === 'object') {
                Object.keys(row).forEach((k) => {
                    if (!seen.has(k)) {
                        seen.add(k);
                        keys.push(k);
                    }
                });
            }
        });
        return keys;
    }

    fetchDefaultReport = () => {
        const { reportName, activeModule, activePage, limit, search } = this.state;
        const normalizedModule = this.normalizeModule(activeModule);

        this.setState({ loading: true });
        axios.get('/api/custom-reports/default/dynamic', {
            params: {
                reportName,
                module: normalizedModule,
                page: activePage,
                limit,
                search,
            }
        })
            .then((res) => {
                const data = res.data || {};
                const reportList = Array.isArray(data.reportList) ? data.reportList : [];
                const headersFromApi = Array.isArray(data.headers) ? data.headers : [];
                const headerKeysFromApi = Array.isArray(data.headerKeys) ? data.headerKeys : headersFromApi;
                this.setState({
                    dynamicHeader: this.resolveDynamicHeaders(headersFromApi, reportList),
                    dynamicHeaderKeys: headerKeysFromApi,
                    dynamicReportList: reportList,
                    count: data.totalCount || 0,
                    loading: false,
                });
            })
            .catch((err) => {
                apiError(err);
                this.setState({ loading: false });
            });
    }

    handleBack = () => {
        this.props.history.push('/reports');
    }

    handleModuleChange = (module) => {
        this.setState({
            activeModule: this.normalizeModule(module),
            activePage: 1,
            search: '',
        }, this.fetchDefaultReport);
    }

    handleSearchChange = (event) => {
        this.setState({
            search: event.target.value,
            activePage: 1,
        }, this.fetchDefaultReport);
    }

    handleExportCsv = () => {
        const { reportName, activeModule } = this.state;
        if (!reportName) {
            return;
        }
        const defaultReportId = `default-${reportName}`;
        axios.get(`/api/custom-reports/export-csv/${encodeURIComponent(defaultReportId)}`, {
            params: {
                type: 'default',
                module: this.normalizeModule(activeModule),
                reportName,
            },
        })
            .then((res) => {
                if (res.data?.message) {
                    toast.success(res.data.message);
                }
            })
            .catch((err) => apiError(err));
    }

    render() {
        const {
            reportTitle,
            availableModules,
            activeModule,
            dynamicHeader,
            dynamicHeaderKeys,
            dynamicReportList,
            loading,
            count,
            search,
            activePage,
            limit,
        } = this.state;

        if (loading && dynamicHeader.length === 0 && dynamicReportList.length === 0) return <Loader />;

        return (
            <div className="content-wrap">
                <div className="breadcrumbbar">
                    <div className="flexRow row">
                        <div className="col-md-8 pl-0">
                            <div className="page-head d-flex align-items-center">
                                <div className="p-0 mr-2 circle-back-arrow" onClick={this.handleBack}>
                                    <PiArrowLeft />
                                </div>
                                <h4 className="page-title mb-0">{reportTitle}</h4>
                            </div>
                        </div>
                        <div className="col-md-4 pr-0">
                            <div className="d-flex justify-content-end">
                                <Button variant="primary" className='btn-with-icon mr-2' onClick={this.handleExportCsv}>
                                    <PiFileCsvDuotone /> Export CSV
                                </Button>
                                {/* <Dropdown className="action-dropdown filter-dropdown">
                                    <Dropdown.Toggle className="filter-dropdown-toggle" id="default-report-preview-actions">
                                        <MoreVertSharpIcon className="dot-icon" />
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={this.handleBack}>Back To Reports</Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown> */}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="contentbar">
                    <div className="filter-without-border">
                        <div className="row align-items-center">
                            <div className="col-md-6 d-flex align-items-center">
                                <div className="default-report-tabs-wrap">
                                    <span className="default-report-sort-label">Sort By</span>
                                    {availableModules.map((module) => (
                                        <Badge
                                            key={module}
                                            variant={activeModule === module ? 'primary' : 'secondary'}
                                            className={`default-report-tab ${activeModule === module ? 'active' : ''}`}
                                            onClick={() => this.handleModuleChange(module)}
                                        >
                                            {this.formatModuleLabel(module)}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="rightFilters">
                                    <Button variant="primary" className="badge-soft-primary mr-2">
                                        Total Records : <span className="font-weight-bold">{count}</span>
                                    </Button>
                                    <div className="positionSearchIcon">
                                        <InputGroup className="search-wrap">
                                            <FormControl
                                                type="text"
                                                placeholder="Search"
                                                value={search}
                                                onChange={this.handleSearchChange}
                                                className="inputSearch mr-sm-2 form-control"
                                            />
                                            <SearchSharpIcon className="search-icon" />
                                        </InputGroup>
                                    </div>
                                    <Dropdown className="d-inline-block">
                                        <Dropdown.Toggle variant="secondary" id="default-report-limit-dropdown" className="dropdown-color-white">
                                            Show {limit}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            {[15, 25, 50, 100].map((nextLimit) => (
                                                <Dropdown.Item
                                                    key={`default-limit-${nextLimit}`}
                                                    onClick={() => this.setState({ limit: nextLimit, activePage: 1 }, this.fetchDefaultReport)}
                                                >
                                                    {nextLimit}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="tableContainerWrapInner responsive-table">
                        <Table>
                            <thead>
                                <tr>
                                    {dynamicHeader.map((header, index) => (
                                        <th key={`${header}-${index}`}>{header}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {dynamicReportList.length > 0 ? dynamicReportList.map((row, rowIndex) => {
                                    const isDeleted = !!(row.isTargetDeleted || row.Email === 'User not found' || row.email === 'User not found');
                                    return (
                                        <tr key={row.id || row._id || rowIndex} className={isDeleted ? 'report-row--deleted' : ''}>
                                            {dynamicHeader.map((header, columnIndex) => {
                                                const value = this.getCellValue(row, dynamicHeaderKeys[columnIndex] || header);
                                                return (
                                                    <td key={`${header}-${columnIndex}`} className='td-detail-page'>
                                                        {isDeleted && columnIndex === 0
                                                            ? <span className="blur-text">{value}</span>
                                                            : value}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                }) : (
                                    <tr>
                                        <td colSpan={dynamicHeader.length || 1}>No data found</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    <div className="pagination-Wrap" style={{ border: '1px solid #dfe2e6', borderTop: 'none' }}>
                        <div className="row">
                            <div className="col-md-6">
                                {!loading ? (
                                    <Pagination
                                        itemClass="page-item"
                                        linkClass="page-link"
                                        activePage={activePage}
                                        itemsCountPerPage={limit}
                                        totalItemsCount={count}
                                        onChange={(pageNumber) => this.setState({ activePage: pageNumber }, this.fetchDefaultReport)}
                                    />
                                ) : <Loader />}
                            </div>
                            <div className="col-md-6 text-right d-flex align-items-center footer-pg">
                                <div className="w-100 pgCount">
                                    <p>
                                        {count === 0 ? 0 : activePage * limit + 1 - limit}-
                                        {activePage * limit > count ? count : activePage * limit} of {count} records
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
